import { useCallback, useDeferredValue, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useDebounce } from "react-use"

import { queries } from "@productlane/zero/queries"

import type { IssueSelectIssue } from "./issue-select"
import type { SuggestedLink } from "./shared/picker-suggestions"
import type { RouterOutputs } from "@/lib/api"
import { api } from "@/lib/api"
import { matchesSearch } from "@/lib/search-match"
import { useSelectedTeamIds } from "@/lib/use-team-filter"
import {
	indexIssues,
	ISSUE_PAGE_SIZE,
	matchesProjectLink,
	nextIssuePage,
	parseIssueSearch,
	rankIssues,
	remoteOnlyIssues,
	titleMatchedIssues,
	wantsRemoteSearch,
} from "./shared/issue-search"
import { pinFirst, resolveSuggestedLinks } from "./shared/picker-suggestions"
import { rankByRelatedness, threadStems } from "./shared/related-to-thread"
import { isSelectableIssue } from "./shared/selectable-issues"
import { isSelectableProject } from "./shared/selectable-projects"
import { sortProjectsByRecency } from "./shared/sort-projects-by-recency"

export const linearIssueToIssue = (
	issue: RouterOutputs["linear"]["issues"]["nodes"][number] & {
		isVisible?: boolean
	},
): IssueSelectIssue => {
	return {
		type: "linear",
		identifier: issue.identifier,
		title: issue.title,
		status: issue.state.type,
		statusColor: issue.state.color,
		statusName: issue.state.name,
		description: issue.description ?? null,
		linearTicketId: issue.id,
		priority: issue.priorityLabel,
		dueDate: issue.dueDate ? new Date(issue.dueDate).getTime() : null,
		team: issue.team.id,
		url: issue.url,
		projectId: issue.projectId ?? null,
		isVisible: issue.isVisible ?? false,
		assigneeId: issue.assignee?.id ?? null,
		assignee: issue.assignee ?? null,
	}
}

const NO_SUGGESTED_LINKS: ReadonlyArray<SuggestedLink> = []

/**
 * Issues and projects a thread can link: synced issues and projects in the workspace's
 * selected Linear teams, plus Linear's own search when those run short or the user
 * scrolls past them. Rows come a page at a time; `loadMore` asks for the next one.
 * With no search, the thread's suggested links come first, then rows that share
 * words with the thread, then the most recently updated rows.
 */
export function useIssueProjectSearch({
	searchQuery,
	issueIds,
	projectIds,
	showProjects,
	suggested = NO_SUGGESTED_LINKS,
	relatedTo,
	enabled = true,
}: {
	searchQuery: string
	issueIds: Array<string>
	projectIds: Array<string>
	showProjects: boolean
	/** The thread's suggested links, best first; shown ahead of everything else with no search. */
	suggested?: ReadonlyArray<SuggestedLink>
	/** The thread the picker links to, to rank related rows first with no search. */
	relatedTo?: { title?: string | null; text?: string | null }
	/** Off while the picker is closed, so syncs don't rebuild the index in the background. */
	enabled?: boolean
}) {
	// Filtering thousands of issues must never hold up the keystroke that triggered it.
	const query = useDeferredValue(searchQuery)
	const term = useMemo(() => parseIssueSearch(query), [query])

	const [debouncedQuery, setDebouncedQuery] = useState(searchQuery)
	useDebounce(
		() => {
			setDebouncedQuery(searchQuery)
		},
		250,
		[searchQuery],
	)
	const remoteText = useMemo(
		() => parseIssueSearch(debouncedQuery).text,
		[debouncedQuery],
	)

	const selectedTeamIds = useSelectedTeamIds()
	const [issues] = useQuery(queries.issues())
	const [projects] = useQuery(queries.projects())

	// Zero returns issues in random id order; recency breaks ties in the ranking.
	const index = useMemo(
		() =>
			enabled
				? indexIssues(
						issues
							.filter(
								(issue) => !!issue.team && selectedTeamIds.includes(issue.team),
							)
							.sort((a, b) => b.updatedAt - a.updatedAt),
					)
				: [],
		[enabled, issues, selectedTeamIds],
	)

	const isEmptySearch = !query.trim()
	const relatedTitle = relatedTo?.title
	const relatedText = relatedTo?.text
	const stems = useMemo(
		() =>
			isEmptySearch && enabled
				? threadStems({ title: relatedTitle, text: relatedText })
				: [],
		[isEmptySearch, enabled, relatedTitle, relatedText],
	)
	const suggestedIssueIds = useMemo(
		() =>
			isEmptySearch
				? suggested.filter((s) => s.kind === "issue").map((s) => s.id)
				: [],
		[isEmptySearch, suggested],
	)
	const suggestedProjectIds = useMemo(
		() =>
			isEmptySearch
				? suggested.filter((s) => s.kind === "project").map((s) => s.id)
				: [],
		[isEmptySearch, suggested],
	)

	const localIssues = useMemo(
		() =>
			pinFirst(
				rankByRelatedness(
					rankIssues(index, term).filter((issue) =>
						isSelectableIssue(issue, query),
					),
					stems,
					(issue) => issue,
				),
				suggestedIssueIds,
				(issue) => issue.id,
			),
		[index, term, query, stems, suggestedIssueIds],
	)

	const filteredProjects = useMemo(() => {
		if (!enabled || !showProjects) return []
		const matching = projects.filter((project) => {
			if (!isSelectableProject(project, selectedTeamIds, query)) return false
			if (term.projectSlugId) {
				return matchesProjectLink(project, term.projectSlugId)
			}
			// A pasted issue link is about one issue, not projects named like it.
			if (term.identifier) return false
			return matchesSearch(term.text, project.name)
		})
		return pinFirst(
			rankByRelatedness(sortProjectsByRecency(matching), stems, (project) => ({
				title: project.name,
				description: project.description,
			})),
			suggestedProjectIds,
			(project) => project.id,
		)
	}, [
		enabled,
		projects,
		showProjects,
		selectedTeamIds,
		query,
		term,
		stems,
		suggestedProjectIds,
	])

	const [reachedEndFor, setReachedEndFor] = useState<string | null>(null)
	const remoteActive = wantsRemoteSearch({
		remoteText,
		localText: term.text,
		localCount: localIssues.length,
		reachedEnd: reachedEndFor === remoteText,
	})
	const remote = api.linear.searchIssues.useInfiniteQuery(
		{ query: remoteText, first: ISSUE_PAGE_SIZE },
		{
			cacheTime: Infinity,
			staleTime: Infinity,
			enabled: remoteActive,
			getNextPageParam: (page) =>
				page.pageInfo.hasNextPage
					? (page.pageInfo.endCursor ?? undefined)
					: undefined,
		},
	)

	const remoteIssues = useMemo(() => {
		if (!remoteActive || !remote.data) return []
		const found = remote.data.pages
			.flatMap((page) => page.nodes)
			.map(linearIssueToIssue)
			.filter((issue) => isSelectableIssue(issue, query))
		return remoteOnlyIssues(localIssues, titleMatchedIssues(found, term))
	}, [remoteActive, remote.data, localIssues, query, term])

	const [paging, setPaging] = useState({ query, limit: ISSUE_PAGE_SIZE })
	const limit = paging.query === query ? paging.limit : ISSUE_PAGE_SIZE
	const issueTotal = localIssues.length + remoteIssues.length
	const total = Math.max(issueTotal, filteredProjects.length)

	const issueRows = useMemo<Array<IssueSelectIssue>>(() => {
		const local = localIssues.slice(0, limit).map((issue) => ({
			...issue,
			type: "local" as const,
			isSelected: issueIds.includes(issue.id),
		}))
		return local.length < limit
			? [...local, ...remoteIssues.slice(0, limit - local.length)]
			: local
	}, [localIssues, remoteIssues, limit, issueIds])

	const projectRows = useMemo(
		() =>
			filteredProjects.slice(0, limit).map((project) => ({
				...project,
				type: "local" as const,
				isSelected: projectIds.includes(project.id),
			})),
		[filteredProjects, limit, projectIds],
	)

	const suggestedRows = useMemo(
		() =>
			isEmptySearch
				? resolveSuggestedLinks(
						suggested,
						issueRows,
						projectRows,
						(issue) => (issue.type === "local" ? issue.id : undefined),
						(project) => project.id,
					)
				: [],
		[isEmptySearch, suggested, issueRows, projectRows],
	)

	const { hasNextPage, isFetchingNextPage, fetchNextPage } = remote
	const loadMore = useCallback(() => {
		const next = nextIssuePage({
			limit,
			total,
			remoteReady: !!remoteText && remoteText === term.text,
			remoteActive,
			hasNextPage: !!hasNextPage,
			isFetchingNextPage,
		})
		if (next.limit !== limit) setPaging({ query, limit: next.limit })
		if (next.reachEnd) setReachedEndFor(remoteText)
		if (next.fetchNext) void fetchNextPage()
	}, [
		limit,
		total,
		remoteText,
		term.text,
		remoteActive,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
		query,
	])

	return {
		debouncedQuery,
		selectedTeamIds,
		isSearchingLinear: remoteActive && remote.isFetching,
		issues: issueRows,
		projects: projectRows,
		suggested: suggestedRows,
		loadMore,
	}
}
